import React, { useState } from 'react';
import { Shield, AlertTriangle, ChevronRight } from 'lucide-react';

const ExamIntegrityTab = ({ flaggedSessions }) => {
  const [selectedViolationSession, setSelectedViolationSession] = useState(null);

  if (flaggedSessions.length === 0) {
    return (
      <div className="border-2 border-gray-200 p-12 text-center">
        <Shield className="w-16 h-16 mx-auto mb-4 text-green-400" />
        <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No Integrity Flags</h3>
        <p className="text-gray-500">All exam sessions are clean — no tab-switch or cheating violations detected.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <AlertTriangle className="text-yellow-600" size={20} />
        <span className="font-semibold text-gray-700">{flaggedSessions.length} flagged session{flaggedSessions.length !== 1 && 's'}</span>
      </div>
      {flaggedSessions.map((s) => (
        <div key={s.session_id} className="border-2 border-gray-200 hover:border-yellow-400 transition-colors" data-testid={`flagged-session-${s.session_id}`}>
          <div
            className="p-4 cursor-pointer flex items-center justify-between"
            onClick={() => setSelectedViolationSession(selectedViolationSession === s.session_id ? null : s.session_id)}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 flex items-center justify-center font-bold text-lg ${
                s.violation_count >= 5 ? 'bg-red-100 text-red-700' :
                s.violation_count >= 3 ? 'bg-yellow-100 text-yellow-700' :
                'bg-orange-50 text-orange-600'
              }`}>
                {s.violation_count}
              </div>
              <div>
                <div className="font-semibold">{s.student_name}</div>
                <div className="text-sm text-gray-500">
                  Grade {s.grade_level} &bull; {s.status} &bull; {new Date(s.started_at).toLocaleDateString()}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {Object.entries(s.violation_types || {}).filter(([t]) => ['tab_switch', 'window_blur'].includes(t)).map(([type, count]) => (
                <span key={type} className="text-xs px-2 py-1 bg-yellow-100 text-yellow-800 font-medium">
                  {type === 'tab_switch' ? 'Tab Switch' : 'Window Blur'}: {count}
                </span>
              ))}
              <ChevronRight size={18} className={`text-gray-400 transition-transform ${selectedViolationSession === s.session_id ? 'rotate-90' : ''}`} />
            </div>
          </div>

          {/* Expanded violation details */}
          {selectedViolationSession === s.session_id && (
            <div className="border-t border-gray-200 p-4 bg-gray-50" data-testid={`violation-details-${s.session_id}`}>
              <h4 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-3">Violation Timeline</h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(s.violations || []).map((v, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm">
                    <div className={`w-6 h-6 flex-shrink-0 flex items-center justify-center text-xs mt-0.5 ${
                      v.type === 'tab_switch' || v.type === 'window_blur' ? 'bg-yellow-100 text-yellow-700' :
                      v.type === 'exam_paused' ? 'bg-red-100 text-red-700' :
                      v.type === 'blocked_shortcut' ? 'bg-purple-100 text-purple-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <span className="font-medium">
                        {v.type === 'tab_switch' ? 'Left exam tab' :
                         v.type === 'tab_return' ? 'Returned to exam' :
                         v.type === 'window_blur' ? 'Window lost focus' :
                         v.type === 'exam_paused' ? 'Exam auto-paused' :
                         v.type === 'exam_resumed' ? 'Exam resumed' :
                         v.type === 'right_click' ? 'Right-click blocked' :
                         v.type === 'copy_attempt' ? 'Copy blocked' :
                         v.type === 'paste_attempt' ? 'Paste blocked' :
                         v.type === 'blocked_shortcut' ? 'Shortcut blocked' :
                         v.type}
                      </span>
                      {v.details?.away_duration_seconds && (
                        <span className="text-gray-500 ml-2">({v.details.away_duration_seconds}s away)</span>
                      )}
                      {v.details?.key && (
                        <span className="text-gray-500 ml-2">(Key: {v.details.key})</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {new Date(v.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ExamIntegrityTab;
